import { Action } from "redux";

import { NewExperienceFormData } from "@/mk2/apps/wiki/containers/ArticleAddExperience/ArticleAddExperiencePage";
import { APIFailureAction, APIRequestAction, APISuccessAction } from "@/mk2/helpers/action";

// API
export interface ArticleAddExperienceApiAction extends Action {
    readonly articleSlug: string;
    readonly categorySlug: string;
}

export const ARTICLE_ADD_EXPERIENCE_REQUEST = "ARTICLE_ADD_EXPERIENCE_REQUEST";

export interface ArticleAddExperienceRequestAction
    extends ArticleAddExperienceApiAction, APIRequestAction {
    readonly type: typeof ARTICLE_ADD_EXPERIENCE_REQUEST;
}

export const ARTICLE_ADD_EXPERIENCE_SUCCESS = "ARTICLE_ADD_EXPERIENCE_SUCCESS";

export interface ArticleAddExperienceSuccessAction
    extends ArticleAddExperienceApiAction, APISuccessAction {
    readonly type: typeof ARTICLE_ADD_EXPERIENCE_SUCCESS;
}

export const ARTICLE_ADD_EXPERIENCE_FAILURE = "ARTICLE_ADD_EXPERIENCE_FAILURE";

export interface ArticleAddExperienceFailureAction
    extends ArticleAddExperienceApiAction, APIFailureAction {
    readonly type: typeof ARTICLE_ADD_EXPERIENCE_FAILURE;
}

export const articleAddExperienceApi = {
    request: (articleSlug: string, categorySlug: string): ArticleAddExperienceRequestAction => ({
        type: ARTICLE_ADD_EXPERIENCE_REQUEST,
        articleSlug,
        categorySlug,
    }),
    success: (
        articleSlug: string,
        categorySlug: string,
        response: any,
    ): ArticleAddExperienceSuccessAction => ({
        type: ARTICLE_ADD_EXPERIENCE_SUCCESS,
        articleSlug,
        categorySlug,
        response,
    }),
    failure: (
        articleSlug: string,
        categorySlug: string,
        error: string,
    ): ArticleAddExperienceFailureAction => ({
        type: ARTICLE_ADD_EXPERIENCE_FAILURE,
        articleSlug,
        categorySlug,
        error,
    }),
};

// Triggers
// ArticleAddExperience submit
export const ARTICLE_ADD_EXPERIENCE_SUBMIT = "ARTICLE_ADD_EXPERIENCE_SUBMIT";

export interface ArticleAddExperienceSubmitAction extends ArticleAddExperienceApiAction {
    readonly type: typeof ARTICLE_ADD_EXPERIENCE_SUBMIT;
    readonly values: NewExperienceFormData;
    readonly categorySlug: string;
    readonly gotoUrl: string;
    // Funnel attribution: which entry point led here (sell_popup / review_hook_*)
    // and the originating bazaar listing. Carried to the submit-success analytics
    // event and forwarded to the backend experience-created event.
    readonly source: string | null;
    readonly fromProductId: number | null;
}

export const articleAddExperienceSubmit = (
    articleSlug,
    categorySlug,
    values,
    gotoUrl,
    source: string | null = null,
    fromProductId: number | null = null,
): ArticleAddExperienceSubmitAction => ({
    type: ARTICLE_ADD_EXPERIENCE_SUBMIT,
    articleSlug,
    categorySlug,
    values,
    gotoUrl,
    source,
    fromProductId,
});

// Unauthenticated submit — saves as PendingAction, then redirects to login/signup
export const ARTICLE_ADD_EXPERIENCE_SUBMIT_UNAUTHENTICATED =
    "ARTICLE_ADD_EXPERIENCE_SUBMIT_UNAUTHENTICATED";

export interface ArticleAddExperienceSubmitUnauthenticatedAction extends Action {
    readonly type: typeof ARTICLE_ADD_EXPERIENCE_SUBMIT_UNAUTHENTICATED;
    readonly articleSlug: string | null;
    readonly categorySlug: string | null;
    readonly values: NewExperienceFormData;
    readonly source: string | null;
    readonly fromProductId: number | null;
}

export const articleAddExperienceSubmitUnauthenticated = (
    articleSlug: string | null,
    categorySlug: string | null,
    values: NewExperienceFormData,
    source: string | null = null,
    fromProductId: number | null = null,
): ArticleAddExperienceSubmitUnauthenticatedAction => ({
    type: ARTICLE_ADD_EXPERIENCE_SUBMIT_UNAUTHENTICATED,
    articleSlug,
    categorySlug,
    values,
    source,
    fromProductId,
});

// Save as draft (admin only) — creates ExperienceDraft and navigates to its edit page
export const ARTICLE_ADD_EXPERIENCE_SAVE_DRAFT = "ARTICLE_ADD_EXPERIENCE_SAVE_DRAFT";

export interface ArticleAddExperienceSaveDraftAction extends Action {
    readonly type: typeof ARTICLE_ADD_EXPERIENCE_SAVE_DRAFT;
    readonly values: NewExperienceFormData;
    readonly articleSlug: string | null;
    readonly categorySlug: string | null;
}

export const articleAddExperienceSaveDraft = (
    values: NewExperienceFormData,
    articleSlug: string | null,
    categorySlug: string | null,
): ArticleAddExperienceSaveDraftAction => ({
    type: ARTICLE_ADD_EXPERIENCE_SAVE_DRAFT,
    values,
    articleSlug,
    categorySlug,
});

// Prefill the photo uploader with an owning bazaar listing's photos (the
// backend verifies request.user == product owner).
export const ARTICLE_ADD_EXPERIENCE_LOAD_PHOTOS = "ARTICLE_ADD_EXPERIENCE_LOAD_PHOTOS";

export interface ArticleAddExperienceLoadPhotosAction extends Action {
    readonly type: typeof ARTICLE_ADD_EXPERIENCE_LOAD_PHOTOS;
    readonly productId: number;
}

export const articleAddExperienceLoadPhotos = (
    productId: number,
): ArticleAddExperienceLoadPhotosAction => ({
    type: ARTICLE_ADD_EXPERIENCE_LOAD_PHOTOS,
    productId,
});

// Dispatched with normalized photo entities so the entities reducer merges them
// into the store (PhotoPreview resolves photos from the entity cache).
export const ARTICLE_ADD_EXPERIENCE_PHOTO_ENTITIES_LOADED =
    "ARTICLE_ADD_EXPERIENCE_PHOTO_ENTITIES_LOADED";
